// src/services/api/creditsApi.js
//
// Read endpoints for the Tenant Credits dashboard (shipped 2026-06-02):
//   GET /api/v1/credits/balance            — this tenant's monthly balance
//   GET /api/v1/credits/usage?group_by=user — per-user usage breakdown
//
// Conventions mirror statsApi.js / runsApi.js:
//   - suppressTenantHeader is CONDITIONAL on `tenant` being supplied. Member /
//     tenant-admin callers rely on X-Tenant-Id from localStorage; superadmin
//     drill-downs pass an explicit ?tenant= which must win over any stale header.
//   - Callers that know the active tenant pass `tenant` to keep the URL
//     tenant-keyed — the BE serves Cache-Control: private, max-age=60 keyed by
//     URL only, so an unscoped URL could leak the previous tenant's numbers
//     after a switch within 60s.
//
// IMPORTANT: every credit/cost field on the wire is a Decimal STRING (lossless).
// Parse with utils/creditsFormat.js before arithmetic — never assume numbers.
//
import { apiFetch } from '../../auth/apiFetch.js';

const CREDITS = '/api/v1/credits';

// The integration doc describes the error envelope as { detail: { error_code,
// message } }, but the rest of this FE (apiFetch.js, statsApi.js, adminApi.js)
// reads error_code/message at the TOP level — implying FastAPI flattens them.
// Parse tolerantly so we surface the friendly message whichever shape arrives.
async function readError(res) {
  const body = await res.json().catch(() => ({}));
  return body && body.detail && typeof body.detail === 'object' ? body.detail : body;
}

export async function fetchCreditBalance({ tenant } = {}) {
  const params = new URLSearchParams();
  if (tenant) params.set('tenant', tenant);
  const qs = params.toString();

  const res = await apiFetch(`${CREDITS}/balance${qs ? `?${qs}` : ''}`, {
    suppressTenantHeader: !!tenant,
  });
  if (!res.ok) {
    const err = await readError(res);
    if (err.error_code === 'TENANT_REQUIRED') {
      throw new Error('Seleziona un tenant per visualizzare il saldo crediti.');
    }
    throw new Error(err.message || `Failed to fetch credit balance: ${res.status}`);
  }
  return res.json();
}

export async function fetchCreditUsage({ tenant } = {}) {
  const params = new URLSearchParams();
  params.set('group_by', 'user');
  if (tenant) params.set('tenant', tenant);

  const res = await apiFetch(`${CREDITS}/usage?${params}`, {
    suppressTenantHeader: !!tenant,
  });
  if (!res.ok) {
    const err = await readError(res);
    if (err.error_code === 'INVALID_GROUP_BY') {
      throw new Error('Dimensione di aggregazione non valida.');
    }
    if (err.error_code === 'UNKNOWN_QUERY_PARAM') {
      throw new Error(`Parametro non riconosciuto: ${err.message || res.status}`);
    }
    throw new Error(err.message || `Failed to fetch credit usage: ${res.status}`);
  }
  return res.json();
}
